import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  writeBatch,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { TALISMAN_REQUIREMENTS, buildClaimableTalismans } from './talismansApi';

const todayStr = () => new Date().toISOString().split('T')[0];

const registerLoginStreak = async (userId) => {
  const ref = doc(db, 'login_streaks', userId);
  const snap = await getDoc(ref);
  const today = todayStr();

  if (!snap.exists()) {
    await setDoc(ref, {
      user_id: userId,
      current_streak: 1,
      total_tokens: 1,
      last_login_date: today,
      created_at: Timestamp.now(),
      updated_at: Timestamp.now(),
    });
    return { current_streak: 1, total_tokens: 1, newToken: true };
  }

  const data = snap.data();
  const lastLogin = new Date(data.last_login_date);
  const diffDays = Math.floor((new Date(today) - lastLogin) / (1000 * 60 * 60 * 24));

  if (diffDays >= 1) {
    const newStreak = diffDays === 1 ? data.current_streak + 1 : 1;
    const newTokens = data.total_tokens + 1;
    await updateDoc(ref, {
      current_streak: newStreak,
      total_tokens: newTokens,
      last_login_date: today,
      updated_at: Timestamp.now(),
    });
    return { current_streak: newStreak, total_tokens: newTokens, newToken: true };
  }

  return {
    current_streak: data.current_streak,
    total_tokens: data.total_tokens,
    newToken: false,
  };
};

const getUserTalismans = async (userId) => {
  const snap = await getDocs(
    query(collection(db, 'user_talismans'), where('user_id', '==', userId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
};

const checkAccess = async (userId) => {
  const userSnap = await getDoc(doc(db, 'users', userId));
  if (!userSnap.exists()) {
    return { hasAccess: true, blocked: false };
  }

  const userData = userSnap.data();
  const now = new Date();
  const hasActiveSubscription =
    userData.subscription_status === 'active' &&
    userData.subscription_end_date &&
    new Date(userData.subscription_end_date) > now;
  const hasActiveTrial =
    userData.subscription_status === 'trial' &&
    userData.trial_end_date &&
    new Date(userData.trial_end_date) > now;
  const hasCancelledSubscription =
    userData.subscription_status === 'cancelled' &&
    userData.subscription_end_date &&
    new Date(userData.subscription_end_date) > now;
  const hasAccess = hasActiveSubscription || hasActiveTrial || hasCancelledSubscription;

  return { hasAccess, blocked: !hasAccess };
};

/** Pobierz dane talizmanów bezpośrednio z Firestore (bez Cloud Functions). */
export const fetchTalismansData = async (userId) => {
  const loginResult = await registerLoginStreak(userId);
  const streakDoc = await getDoc(doc(db, 'login_streaks', userId));
  const streak = streakDoc.exists()
    ? streakDoc.data()
    : { current_streak: 0, total_tokens: 0 };

  const talismans = await getUserTalismans(userId);
  const ownedTalismans = talismans.map((t) => Number(t.talisman_id));
  const activeTalisman = talismans.find((t) => t.active) || null;
  const totalTokens = streak.total_tokens || 0;
  const access = await checkAccess(userId);

  return {
    success: true,
    streak: {
      current_streak: streak.current_streak || 0,
      total_tokens: totalTokens,
      newToken: loginResult.newToken,
    },
    eligibility: {
      totalTokens,
      nextTalisman: TALISMAN_REQUIREMENTS.find((req) => req > totalTokens) || null,
      ownedTalismans,
      claimableTalismans: buildClaimableTalismans(totalTokens, ownedTalismans),
      hasAccess: access.hasAccess,
      blocked: access.blocked,
    },
    talismans,
    activeTalisman,
    bonuses: [],
  };
};

export const grantTalismanFirestore = async (userId, talismanId) => {
  const required = TALISMAN_REQUIREMENTS[Number(talismanId) - 1];
  if (!required) {
    throw new Error('Nieprawidłowy talizman');
  }

  const streakDoc = await getDoc(doc(db, 'login_streaks', userId));
  const totalTokens = streakDoc.exists() ? streakDoc.data().total_tokens || 0 : 0;
  if (totalTokens < required) {
    throw new Error('Za mało żetonów');
  }

  const existing = await getDocs(
    query(
      collection(db, 'user_talismans'),
      where('user_id', '==', userId),
      where('talisman_id', '==', Number(talismanId))
    )
  );
  if (!existing.empty) {
    throw new Error('Już posiadasz ten talizman');
  }

  await addDoc(collection(db, 'user_talismans'), {
    user_id: userId,
    talisman_id: Number(talismanId),
    owned: true,
    active: false,
    obtained_date: Timestamp.now(),
    created_at: Timestamp.now(),
  });

  return { success: true, message: 'Talizman został odebrany!' };
};

export const toggleTalismanFirestore = async (userId, talismanId, active) => {
  const allSnap = await getDocs(
    query(collection(db, 'user_talismans'), where('user_id', '==', userId))
  );
  const batch = writeBatch(db);
  allSnap.docs.forEach((d) => batch.update(d.ref, { active: false }));

  if (active) {
    const target = allSnap.docs.find(
      (d) => Number(d.data().talisman_id) === Number(talismanId)
    );
    if (!target) {
      throw new Error('Nie posiadasz tego talizmanu');
    }
    batch.update(target.ref, { active: true });
  }

  await batch.commit();
  return {
    success: true,
    message: active ? 'Talizman został aktywowany!' : 'Talizman został deaktywowany!',
  };
};
